import React, { useState, useMemo } from 'react';
import './PurchasedAsics.css';
import { useLanguage } from '../../context/LanguageContext';
import { getTranslation } from '../../i18n/translations';
import '../../components/Button/Button.css';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const PurchasedAsics = () => {
  const { language } = useLanguage();
  const [chartPage, setChartPage] = useState(1);
  const [tablePage, setTablePage] = useState(1);
  const itemsPerPage = 15; // 15 data points per page
  const totalChartPages = 5;

  // Mock data
  const totalReward = {
    btc: '0.00000000',
    usd: '0',
    monthlyBtc: '0.00000000'
  };

  const asicsCount = 0;
  const totalMiningPower = 0; // TH/s

  // Empty array for now - table is empty in HTML
  const purchasedAsics = [];
  const totalTablePages = 1;

  const handleAddAsics = () => {
    window.history.pushState({ page: 'buy-asics' }, '', '/mining-store/buy-asics');
    window.location.reload();
  };

  const handleAddMore = () => {
    window.history.pushState({ page: 'buy-asics' }, '', '/mining-store/buy-asics');
    window.location.reload();
  };

  // Generate chart data based on current page
  const chartData = useMemo(() => {
    // Generate dates starting from Dec 30 (based on HTML structure - shows dates from 30.12 to 12.01)
    const startDate = new Date(2025, 11, 30); // Month is 0-indexed, so 11 = December, year 2025
    const data = [];
    
    const baseValues = Array(15).fill(0); // All zeros based on the HTML chart
    
    for (let i = 0; i < itemsPerPage; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + (chartPage - 1) * itemsPerPage + i);
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const dateStr = `${day}.${month}`;
      
      // Use base values (all zeros for empty chart)
      const value = baseValues[i] || 0;
      
      data.push({
        date: dateStr,
        value: value
      });
    }
    
    return data;
  }, [chartPage, itemsPerPage]);

  // Chart configuration
  const chartOptions = useMemo(() => {
    const startDate = new Date(2025, 11, 30);
    const endDate = new Date(2026, 0, 12);
    const dateRange = `<${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} - ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}>`;
    
    return {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        spacing: [10, 10, 10, 10],
        height: 400
      },
      title: {
        text: ''
      },
      xAxis: {
        categories: chartData.map(item => item.date),
        labels: {
          rotation: -90,
          align: 'right',
          style: {
            fontSize: '0.8em',
            color: '#333333'
          }
        },
        lineColor: '#333333',
        lineWidth: 1,
        tickColor: '#333333'
      },
      yAxis: {
        title: {
          text: ''
        },
        labels: {
          format: '{value}',
          style: {
            fontSize: '0.8em',
            color: '#333333'
          }
        },
        gridLineColor: '#e6e6e6',
        gridLineWidth: 1
      },
      plotOptions: {
        column: {
          borderRadius: 3,
          borderWidth: 1,
          borderColor: '#ffffff',
          color: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, '#96E6C2'],
              [1, '#E0F7ED']
            ]
          }
        }
      },
      series: [{
        name: 'Mining Output',
        data: chartData.map(item => item.value),
        showInLegend: false
      }],
      tooltip: {
        enabled: true,
        formatter: function() {
          const index = this.x;
          const dataPoint = chartData[index];
          return `<b>${dataPoint.date}</b><br/>Value: ${this.y}`;
        },
        backgroundColor: '#ffffff',
        borderColor: '#96E6C2',
        borderRadius: 3,
        style: {
          fontSize: '0.8em',
          color: '#333333'
        }
      },
      credits: {
        enabled: false
      }
    };
  }, [chartData]);

  const handlePreviousChartPage = () => {
    setChartPage(prev => Math.max(1, prev - 1));
  };

  const handleNextChartPage = () => {
    setChartPage(prev => Math.min(totalChartPages, prev + 1));
  };

  const handlePreviousTablePage = () => {
    setTablePage(prev => Math.max(1, prev - 1));
  };

  const handleNextTablePage = () => {
    setTablePage(prev => Math.min(totalTablePages, prev + 1));
  };

  // Date range for chart
  const startDate = new Date(2025, 11, 30);
  const endDate = new Date(2026, 0, 12);
  const dateRange = `<${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} - ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}>`;

  return (
    <div className="purchased">
      <div className="purchased__header">
        <svg
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_4682_11548)">
            <rect x="0.43125" y="1.025" width="22.95" height="22.95" rx="4.275" stroke="#1570EF" strokeWidth="1.05"></rect>
            <path
              d="M12.675 7.83333C12.675 8.56971 11.0129 9.16667 8.9625 9.16667C6.91214 9.16667 5.25 8.56971 5.25 7.83333M12.675 7.83333C12.675 7.09695 11.0129 6.5 8.9625 6.5C6.91214 6.5 5.25 7.09695 5.25 7.83333M12.675 7.83333V8.83333M5.25 7.83333V15.8333C5.25 16.5697 6.91214 17.1667 8.9625 17.1667M8.9625 11.8333C8.84873 11.8333 8.73616 11.8315 8.625 11.8279C6.73281 11.7667 5.25 11.1955 5.25 10.5M8.9625 14.5C6.91214 14.5 5.25 13.903 5.25 13.1667M18.75 12.1667C18.75 12.903 17.0879 13.5 15.0375 13.5C12.9871 13.5 11.325 12.903 11.325 12.1667M18.75 12.1667C18.75 11.4303 17.0879 10.8333 15.0375 10.8333C12.9871 10.8333 11.325 11.4303 11.325 12.1667M18.75 12.1667V17.1667C18.75 17.903 17.0879 18.5 15.0375 18.5C12.9871 18.5 11.325 17.903 11.325 17.1667V12.1667M18.75 14.6667C18.75 15.403 17.0879 16 15.0375 16C12.9871 16 11.325 15.403 11.325 14.6667"
              stroke="#1570EF"
              strokeWidth="1.05"
              strokeLinecap="round"
              strokeLinejoin="round"
            ></path>
          </g>
          <defs>
            <clipPath id="clip0_4682_11548">
              <rect width="24" height="24" fill="white" transform="translate(0 0.5)"></rect>
            </clipPath>
          </defs>
        </svg>
        <h1 className="purchased__title">{getTranslation('purchasedAsics', language)}</h1>
      </div>

      <div className="purchased__block">
        <div className="purchased__card-block">
          <h2 className="purchased__title-block">{getTranslation('overview', language)}</h2>
          <div className="purchased__card">
            {/* Total Mining Reward */}
            <div className="overview-purchased overview-purchased_second">
              <p className="overview-purchased__title">{getTranslation('totalMiningReward', language)}</p>
              <div>
                <div className="overview-purchased__crypto">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    className="overview__image"
                    alt="crypto-logo"
                  >
                    <rect
                      x="1.07676"
                      y="0.525"
                      width="22.95"
                      height="22.95"
                      rx="5.475"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                    />
                    <path
                      d="M14.9702 7.00768L15.4347 5.2647L15.8701 5.38115L15.3963 7.13586L15.2599 7.6412L15.7698 7.7541L15.77 7.75414L15.7703 7.75423L15.7706 7.75429L15.7709 7.75436L15.7719 7.75458L15.7852 7.75779C15.798 7.76096 15.8186 7.76621 15.846 7.77373C15.9008 7.7888 15.9823 7.81286 16.083 7.84743C16.2852 7.91691 16.5594 8.02703 16.8469 8.18861C17.4304 8.51652 18.0105 9.02189 18.2285 9.77565C18.4428 10.5164 18.2135 11.0531 17.8789 11.4282C17.7058 11.6223 17.5033 11.7724 17.3185 11.8746C17.2266 11.9254 17.1433 11.962 17.0754 11.9858C17.0416 11.9976 17.0136 12.0055 16.992 12.0105C16.9812 12.013 16.9728 12.0146 16.9666 12.0156L16.9595 12.0166L16.9576 12.0168C16.9573 12.0168 16.9571 12.0168 16.957 12.0168L16.957 12.0168L15.2581 12.1352L16.7494 12.953L16.75 12.9533L16.7502 12.9535L16.7597 12.959C16.7694 12.9647 16.7854 12.9744 16.8068 12.9881C16.8496 13.0156 16.9132 13.0587 16.989 13.1172C17.1417 13.2351 17.3368 13.4103 17.5114 13.6396C17.8516 14.0864 18.1178 14.736 17.8331 15.6301C17.5379 16.5575 16.9562 17.0793 16.2081 17.3351C15.4337 17.5997 14.4497 17.5873 13.386 17.3502L12.9149 17.2451L12.7933 17.7122L12.3158 19.5465L11.8832 19.4308L12.3691 17.6291L12.4989 17.1476L12.0178 17.0165L10.9051 16.7132L10.4205 16.5812L10.2906 17.0663L9.80368 18.8847L9.37819 18.7703L9.86544 16.9625L9.99553 16.4798L9.51289 16.3496L7.24871 15.7389L7.54609 15.0786C7.555 15.081 7.56396 15.0834 7.57298 15.0858C7.80901 15.149 8.09103 15.2234 8.25005 15.2613C8.62847 15.3515 8.92916 15.1787 9.11139 14.9742C9.27946 14.7855 9.37658 14.5465 9.43001 14.3493C9.46647 14.2146 9.70526 13.2523 9.98043 12.1433C10.0402 11.9025 10.1017 11.6548 10.1632 11.4071C10.5113 10.0046 10.8589 8.60883 10.9166 8.40463C10.9854 8.16082 11.0408 7.83655 10.9184 7.52615C10.7806 7.17695 10.4775 6.98074 10.1247 6.88914C9.89892 6.83052 9.63393 6.75631 9.41071 6.69238L9.553 6.17064L11.8301 6.78044L12.3128 6.90972L12.4423 6.42701L12.9287 4.61387L13.3423 4.72608L12.8795 6.44687L12.7496 6.92984L13.2326 7.0596L14.3573 7.36178L14.8411 7.49178L14.9702 7.00768ZM11.9834 12.4545L12.0524 11.9593L11.608 11.8974L11.4985 12.3326L10.7349 15.3686L10.6245 15.8073L11.05 15.9608L11.2198 15.4905C11.05 15.9608 11.0501 15.9609 11.0503 15.9609L11.0506 15.9611L11.0516 15.9614L11.0546 15.9625L11.0646 15.966L11.1003 15.9784C11.1309 15.9889 11.1748 16.0037 11.2303 16.0215C11.341 16.0571 11.4984 16.1053 11.6884 16.1567C12.066 16.2589 12.5831 16.3767 13.1235 16.4311C13.655 16.4845 14.2565 16.4819 14.7784 16.3064C15.3254 16.1224 15.8041 15.737 15.9755 15.0563C16.1471 14.3748 15.9069 13.8098 15.5097 13.3912C15.1314 12.9925 14.6009 12.7099 14.1063 12.5097C13.6033 12.306 13.0907 12.1693 12.7089 12.0836C12.5168 12.0406 12.355 12.0098 12.2403 11.9897C12.1829 11.9797 12.1372 11.9722 12.1052 11.9672L12.0678 11.9615L12.0572 11.96L12.0541 11.9596L12.0531 11.9594L12.0527 11.9594C12.0526 11.9594 12.0524 11.9593 11.9834 12.4545ZM11.8519 10.8845L11.7175 11.3832L12.2203 11.5013C12.306 11.5214 12.3974 11.5437 12.4933 11.5671C13.0697 11.7077 13.8105 11.8884 14.4673 11.8812C14.8603 11.8769 15.2781 11.8072 15.6398 11.5843C16.0169 11.352 16.2827 10.9843 16.4112 10.486C16.563 9.89723 16.3527 9.40309 16.0209 9.0388C15.705 8.69188 15.2641 8.44011 14.8586 8.25962C14.4452 8.0756 14.0247 7.94668 13.7121 7.86416C13.5547 7.8226 13.422 7.79211 13.3278 7.77186C13.2806 7.76172 13.2429 7.75411 13.2165 7.74893L13.1854 7.74297L13.1765 7.74132L13.1738 7.74083L13.1728 7.74066L13.1725 7.7406C13.1724 7.74057 13.1722 7.74055 13.0841 8.23273L13.1722 7.74055L12.7207 7.65972L12.6013 8.10266L11.8519 10.8845Z"
                      stroke="#1570EF"
                    />
                  </svg>
                  <div className="overview-purchased__crypto-block">
                    <p className="overview-purchased__power-btc">{totalReward.btc}</p>
                    <p className="overview-purchased__power-btc">BTC</p>
                  </div>
                  <span className="overview-purchased__power-usd">/</span>
                  <div className="overview-purchased__crypto-block">
                    <p className="overview-purchased__power-usd">{totalReward.usd}</p>
                    <p className="overview-purchased__power-usd">USD</p>
                  </div>
                </div>
                <div>
                  <div className="overview-purchased__crypto-block">
                    <p className="overview-purchased__power-btc-month">
                      + {totalReward.monthlyBtc} BTC
                    </p>
                    <p className="overview-purchased__month">{getTranslation('inOneMonth', language)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ASICs */}
            <div className="overview-purchased">
              <div className="overview-purchased__title-block">
                <p className="overview-purchased__title">{getTranslation('asics', language)}</p>
              </div>
              <div className="overview-purchased__block">
                <div className="overview-purchased__power">
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    alt="logo"
                  >
                    <rect
                      x="0.858496"
                      y="0.525"
                      width="22.95"
                      height="22.95"
                      rx="5.475"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                    />
                    <path
                      d="M15.5418 8.79167L9.12516 8.79167M9.8835 6.75H14.7835C15.7636 6.75 16.2536 6.75 16.628 6.94074C16.9573 7.10852 17.225 7.37623 17.3928 7.70552C17.5835 8.07986 17.5835 8.56991 17.5835 9.55V14.45C17.5835 15.4301 17.5835 15.9201 17.3928 16.2945C17.225 16.6238 16.9573 16.8915 16.628 17.0593C16.2536 17.25 15.7636 17.25 14.7835 17.25H9.8835C8.9034 17.25 8.41336 17.25 8.03901 17.0593C7.70973 16.8915 7.44201 16.6238 7.27423 16.2945C7.0835 15.9201 7.0835 15.4301 7.0835 14.45V9.55C7.0835 8.56991 7.0835 8.07986 7.27423 7.70552C7.44201 7.37623 7.70973 7.10852 8.03901 6.94074C8.41336 6.75 8.9034 6.75 9.8835 6.75Z"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.12939 10.8574L15.5373 10.8575"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                    />
                    <circle cx="12.3335" cy="14.623" r="0.875" fill="#1570EF" />
                  </svg>
                  <p className="overview-purchased__power-value">
                    {asicsCount} <span className="overview-purchased__power-title_contract">{getTranslation('asics', language)}</span>
                  </p>
                </div>
                <button className="primary" type="button" onClick={handleAddAsics}>
                  {getTranslation('addAsics', language)}
                </button>
              </div>
            </div>

            {/* Total Mining Power */}
            <div className="overview-purchased">
              <div className="overview-purchased__title-block">
                <p className="overview-purchased__title">{getTranslation('totalMiningPower', language)}</p>
              </div>
              <div className="overview-purchased__block">
                <div className="overview-purchased__power">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    viewBox="0 0 25 25"
                    fill="none"
                    alt="logo"
                  >
                    <rect
                      x="0.775"
                      y="0.762793"
                      width="22.95"
                      height="22.95"
                      rx="5.475"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                    />
                    <path
                      d="M13.1751 6.23779L7.83119 12.6505C7.6219 12.9016 7.51726 13.0272 7.51566 13.1333C7.51427 13.2255 7.55535 13.3132 7.62707 13.3711C7.70956 13.4378 7.87302 13.4378 8.19993 13.4378H12.5751L11.9751 18.2378L17.319 11.8251C17.5283 11.5739 17.633 11.4484 17.6346 11.3423C17.636 11.2501 17.5949 11.1624 17.5232 11.1045C17.4407 11.0378 17.2772 11.0378 16.9503 11.0378H12.5751L13.1751 6.23779Z"
                      stroke="#1570EF"
                      strokeWidth="1.05"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="overview-purchased__power-value">
                    {totalMiningPower} <span className="overview-purchased__power-title">TH/s</span>
                  </p>
                </div>
                <button className="primary" type="button" onClick={handleAddMore}>
                  {getTranslation('addMore', language)}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mining Output Section */}
        <div className="mining-output">
          <div className="mining-output__title-block">
            <h2 className="mining-output__title">{getTranslation('miningOutput', language)}</h2>
          </div>
          <div className="mining-output__result">
            <div className="mining-output-result__block">
              <div className="mining-output-result__chart">
                <div className="date-range">{dateRange}</div>
                <div className="chart">
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* My ASICs Table */}
        <div className="devices__contain">
          <h2 className="devices__title">{getTranslation('myAsics', language)}</h2>
          <div className="devices-container">
            <table>
              <thead>
                <tr>
                  <th>{getTranslation('deviceId', language)}</th>
                  <th>{getTranslation('model', language)}</th>
                  <th>{getTranslation('latestHashrate', language)}</th>
                  <th>{getTranslation('status', language)}</th>
                  <th>{getTranslation('hostingPaymentDate', language)}</th>
                  <th>{getTranslation('actions', language)}</th>
                </tr>
              </thead>
              <tbody>
                {purchasedAsics.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#667085' }}>
                      {getTranslation('noPurchasedAsics', language)}
                    </td>
                  </tr>
                ) : (
                  purchasedAsics.map((asic) => (
                    <tr key={asic.deviceId}>
                      <td>{asic.deviceId}</td>
                      <td>{asic.model}</td>
                      <td>{asic.latestHashrate}</td>
                      <td>{asic.status}</td>
                      <td>{asic.hostingPaymentDate}</td>
                      <td>{asic.actions}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className="paginator-btn">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="paginator-btn__previous"
                onClick={handlePreviousTablePage}
                style={{ cursor: tablePage === 1 ? 'not-allowed' : 'pointer', opacity: tablePage === 1 ? 0.5 : 1 }}
                disabled={tablePage === 1}
              >
                <path
                  d="M15.8334 10H4.16675M4.16675 10L10.0001 15.8333M4.16675 10L10.0001 4.16666"
                  stroke="#667085"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="paginator-btn__current-total">{tablePage} of {totalTablePages}</p>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="paginator-btn__next"
                onClick={handleNextTablePage}
                style={{ cursor: tablePage === totalTablePages ? 'not-allowed' : 'pointer', opacity: tablePage === totalTablePages ? 0.5 : 1 }}
                disabled={tablePage === totalTablePages}
              >
                <path
                  d="M4.16675 10H15.8334M15.8334 10L10.0001 4.16666M15.8334 10L10.0001 15.8333"
                  stroke="#667085"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchasedAsics;

